/**
 * Calendar view for orders: week selector with events grouped by day.
 * Similar to SchedulePage but shows events with order counts.
 */

import { useMemo, useCallback, useState, useEffect } from "react";
import { Link } from "react-router-dom";
import {
  IconCalendar,
  IconChevronLeft,
  IconChevronRight,
  IconChevronDown,
  IconDocument,
  IconTicket,
  IconClock,
} from "../../../components/Icons.tsx";
import {
  getTodayDateKeyBangkok,
  getWeekStartDateKeyBangkok,
  addDaysToDateKey,
  getDayOfMonthAndWeekdayBangkok,
  formatTimeBangkok,
  type BangkokDateKey,
} from "../../../lib/bangkok-time.ts";
import { APP_TIMEZONE } from "../../../config.ts";
import type { AdminEventWithStats } from "../shared/types.ts";
import { useAdminEventsWithStats } from "./hooks/useAdminEventsWithStats";

const WEEKDAYS_SHORT = ["Пн", "Вт", "Ср", "Чт", "Пт", "Сб", "Вс"];
const WEEKDAYS_FULL = [
  "Понедельник",
  "Вторник",
  "Среда",
  "Четверг",
  "Пятница",
  "Суббота",
  "Воскресенье",
];
const MONTHS_GENITIVE = [
  "января",
  "февраля",
  "марта",
  "апреля",
  "мая",
  "июня",
  "июля",
  "августа",
  "сентября",
  "октября",
  "ноября",
  "декабря",
];

const CALENDAR_WEEK_STORAGE_KEY = "admin_orders_calendar_week";
const CALENDAR_SELECTED_STORAGE_KEY = "admin_orders_calendar_selected";

function isValidDateKey(s: string): s is BangkokDateKey {
  return /^\d{4}-\d{2}-\d{2}$/.test(s);
}

interface WeekDaySelectorProps {
  weekDayDateKeys: BangkokDateKey[];
  selectedDateKey: BangkokDateKey | null; // null means whole week selected
  onSelectDateKey: (dateKey: BangkokDateKey | null) => void;
  eventsByDay: Map<string, AdminEventWithStats[]>;
  todayDateKey: BangkokDateKey;
}

function WeekDaySelector({
  weekDayDateKeys,
  selectedDateKey,
  onSelectDateKey,
  eventsByDay,
  todayDateKey,
}: WeekDaySelectorProps) {
  const isWeekSelected = selectedDateKey === null;

  return (
    <div className="flex gap-1 xs:gap-1.5 lg-mobile:gap-2">
      {weekDayDateKeys.map((dateKey) => {
        const dayEvents = eventsByDay.get(dateKey) || [];
        const hasEvents = dayEvents.length > 0;
        const totalPending = dayEvents.reduce(
          (sum, e) => sum + e.pendingCount,
          0
        );
        const isSelected = dateKey === selectedDateKey;
        const isTodayDate = dateKey === todayDateKey;
        const { dayOfMonth, weekdayIndex } =
          getDayOfMonthAndWeekdayBangkok(dateKey);

        return (
          <button
            key={dateKey}
            type="button"
            onClick={() => onSelectDateKey(dateKey)}
            className={`
              flex-1 flex flex-col items-center py-2 xs:py-2.5 lg-mobile:py-3 px-1 
              rounded-xl lg-mobile:rounded-2xl transition-all relative
              ${
                isSelected
                  ? "bg-[var(--color-accent)] text-white shadow-md"
                  : isWeekSelected
                  ? hasEvents
                    ? "bg-teal-100 text-teal-900"
                    : "bg-slate-100 text-slate-600"
                  : hasEvents
                  ? "bg-teal-50 text-teal-900 hover:bg-teal-100"
                  : "bg-slate-50 text-slate-600 hover:bg-slate-100"
              }
              ${
                isTodayDate && !isSelected && !isWeekSelected
                  ? "ring-2 ring-[var(--color-accent)] ring-offset-1"
                  : ""
              }
            `}
          >
            <span className="text-[10px] xs:text-xs lg-mobile:text-sm font-medium opacity-70">
              {WEEKDAYS_SHORT[weekdayIndex]}
            </span>
            <span className="text-base xs:text-lg lg-mobile:text-xl font-bold mt-0.5">
              {dayOfMonth}
            </span>
            {hasEvents && (
              <span
                className={`
                  mt-1 w-1.5 h-1.5 lg-mobile:w-2 lg-mobile:h-2 rounded-full
                  ${
                    isSelected
                      ? "bg-white/80"
                      : isWeekSelected
                      ? "bg-teal-500"
                      : totalPending > 0
                      ? "bg-amber-500"
                      : "bg-teal-500"
                  }
                `}
              />
            )}
            {totalPending > 0 && !isSelected && !isWeekSelected && (
              <span className="absolute -top-1 -right-1 w-4 h-4 bg-amber-500 text-white text-[10px] font-bold rounded-full flex items-center justify-center">
                {totalPending > 9 ? "9+" : totalPending}
              </span>
            )}
          </button>
        );
      })}
    </div>
  );
}

interface EventItemProps {
  event: AdminEventWithStats;
}

function EventItem({ event }: EventItemProps) {
  const timeStr = formatTimeBangkok(event.startsAt);

  return (
    <Link
      to={`/admin/events/${event.id}/orders`}
      className="flex items-center gap-3 py-3 px-4 -mx-4 active:bg-slate-50 transition-colors"
    >
      {/* Time column */}
      <div className="w-14 flex flex-col items-center justify-center shrink-0">
        <IconClock className="w-4 h-4 text-slate-400 mb-0.5" />
        <span className="text-[13px] font-semibold text-slate-700">
          {timeStr}
        </span>
      </div>

      {/* Event info */}
      <div className="flex-1 min-w-0">
        <h3 className="font-medium text-[15px] text-slate-900 truncate">
          {event.title}
        </h3>
        <div className="flex items-center gap-3 mt-1">
          <span className="flex items-center gap-1 text-[12px] text-slate-600">
            <IconDocument className="w-3.5 h-3.5 text-slate-400" />
            {event.ordersCount}
          </span>
          <span className="flex items-center gap-1 text-[12px] text-slate-600">
            <IconTicket className="w-3.5 h-3.5 text-slate-400" />
            {event.ticketsCount}
          </span>
          {event.pendingCount > 0 && (
            <span className="flex items-center gap-1 text-[12px] text-amber-600 font-medium">
              {event.pendingCount} ждут
            </span>
          )}
        </div>
      </div>

      <IconChevronRight className="w-5 h-5 text-slate-300 shrink-0" />
    </Link>
  );
}

interface DayAgendaProps {
  selectedDateKey: BangkokDateKey | null; // null means whole week
  events: AdminEventWithStats[];
  todayDateKey: BangkokDateKey;
  weekLabel?: string;
}

function DayAgenda({
  selectedDateKey,
  events,
  todayDateKey,
  weekLabel,
}: DayAgendaProps) {
  const isWeekView = selectedDateKey === null;
  const dayName = selectedDateKey
    ? WEEKDAYS_FULL[
        getDayOfMonthAndWeekdayBangkok(selectedDateKey).weekdayIndex
      ]
    : "";
  const isTodayDate = selectedDateKey === todayDateKey;

  if (events.length === 0) {
    return (
      <div className="card p-6 lg-mobile:p-8 text-center">
        <IconCalendar className="w-12 h-12 lg-mobile:w-16 lg-mobile:h-16 mx-auto mb-3 lg-mobile:mb-4 text-slate-200" />
        <p className="text-slate-500 text-sm lg-mobile:text-base">
          {isWeekView
            ? "Нет мероприятий на этой неделе"
            : isTodayDate
            ? "Сегодня нет мероприятий"
            : "Нет мероприятий"}
        </p>
        {!isWeekView && (
          <p className="text-slate-400 text-xs lg-mobile:text-sm mt-1">
            {dayName}
          </p>
        )}
      </div>
    );
  }

  return (
    <div className="card">
      <div className="flex items-center justify-between px-4 pt-4 pb-2">
        <h2 className="text-sm xs:text-base lg-mobile:text-lg font-semibold text-slate-800">
          {isWeekView
            ? `Неделя ${weekLabel}`
            : isTodayDate
            ? "Сегодня"
            : dayName}
        </h2>
        <span className="text-xs lg-mobile:text-sm text-slate-500">
          {events.length}{" "}
          {events.length === 1
            ? "мероприятие"
            : events.length < 5
            ? "мероприятия"
            : "мероприятий"}
        </span>
      </div>

      <div className="divide-y divide-slate-100">
        {events.map((event) => (
          <EventItem key={event.id} event={event} />
        ))}
      </div>
    </div>
  );
}

export function OrdersCalendarView() {
  const todayDateKey = getTodayDateKeyBangkok();
  const [weekStartDateKey, setWeekStartDateKey] = useState(() => {
    const saved = sessionStorage.getItem(CALENDAR_WEEK_STORAGE_KEY);
    if (saved && isValidDateKey(saved)) return saved;
    return getWeekStartDateKeyBangkok();
  });
  // null means whole week is selected
  const [selectedDateKey, setSelectedDateKey] = useState<BangkokDateKey | null>(
    () => {
      const saved = sessionStorage.getItem(CALENDAR_SELECTED_STORAGE_KEY);
      if (saved === "week") return null;
      if (saved && isValidDateKey(saved)) return saved;
      return getTodayDateKeyBangkok();
    }
  );

  // Calculate date range for the week
  const toDateKey = addDaysToDateKey(weekStartDateKey, 6);

  // URQL hook for fetching events
  const {
    events,
    isLoading,
    error: eventsError,
    refetch: refetchEvents,
  } = useAdminEventsWithStats({
    fromDate: weekStartDateKey,
    toDate: toDateKey,
    timezone: APP_TIMEZONE,
  });

  const error = eventsError?.message ?? null;

  // Save calendar state to sessionStorage
  useEffect(() => {
    sessionStorage.setItem(CALENDAR_WEEK_STORAGE_KEY, weekStartDateKey);
    sessionStorage.setItem(
      CALENDAR_SELECTED_STORAGE_KEY,
      selectedDateKey === null ? "week" : selectedDateKey
    );
  }, [weekStartDateKey, selectedDateKey]);

  const weekDayDateKeys = useMemo(
    () =>
      Array.from({ length: 7 }, (_, i) =>
        addDaysToDateKey(weekStartDateKey, i)
      ),
    [weekStartDateKey]
  );

  // Group events by day
  const eventsByDay = useMemo(() => {
    const map = new Map<string, AdminEventWithStats[]>();
    for (const event of events) {
      // Parse date in Bangkok timezone
      const date = new Date(event.startsAt);
      const dateKey = date.toLocaleDateString("sv-SE", {
        timeZone: APP_TIMEZONE,
      }) as BangkokDateKey;

      // Safe pattern: get existing array or create new one
      const existing = map.get(dateKey);
      if (existing) {
        existing.push(event);
      } else {
        map.set(dateKey, [event]);
      }
    }
    // Sort events within each day by time
    for (const dayEvents of map.values()) {
      dayEvents.sort(
        (a, b) =>
          new Date(a.startsAt).getTime() - new Date(b.startsAt).getTime()
      );
    }
    return map;
  }, [events]);

  // Get events for selected day or whole week
  const selectedEvents = useMemo(() => {
    if (selectedDateKey === null) {
      // Whole week selected - return all events sorted by time
      return [...events].sort(
        (a, b) =>
          new Date(a.startsAt).getTime() - new Date(b.startsAt).getTime()
      );
    }
    return eventsByDay.get(selectedDateKey) || [];
  }, [selectedDateKey, eventsByDay, events]);

  // Week label
  const weekLabel = useMemo(() => {
    const startKey = weekStartDateKey;
    const endKey = addDaysToDateKey(weekStartDateKey, 6);
    const [, startM] = startKey.split("-").map(Number);
    const [, endM] = endKey.split("-").map(Number);
    const startDay = startKey.split("-")[2];
    const endDay = endKey.split("-")[2];
    if (startM === endM) {
      return `${startDay}–${endDay} ${MONTHS_GENITIVE[startM - 1]}`;
    }
    return `${startDay} ${MONTHS_GENITIVE[startM - 1]} – ${endDay} ${
      MONTHS_GENITIVE[endM - 1]
    }`;
  }, [weekStartDateKey]);

  const isCurrentWeek = weekStartDateKey === getWeekStartDateKeyBangkok();
  const isWeekSelected = selectedDateKey === null;

  // Navigation handlers
  const goToPrevWeek = useCallback(() => {
    setWeekStartDateKey((prev) => addDaysToDateKey(prev, -7));
    if (selectedDateKey !== null) {
      setSelectedDateKey((prev) => (prev ? addDaysToDateKey(prev, -7) : null));
    }
  }, [selectedDateKey]);

  const goToNextWeek = useCallback(() => {
    setWeekStartDateKey((prev) => addDaysToDateKey(prev, 7));
    if (selectedDateKey !== null) {
      setSelectedDateKey((prev) => (prev ? addDaysToDateKey(prev, 7) : null));
    }
  }, [selectedDateKey]);

  const goToToday = useCallback(() => {
    setWeekStartDateKey(getWeekStartDateKeyBangkok());
    setSelectedDateKey(getTodayDateKeyBangkok());
  }, []);

  const selectWholeWeek = useCallback(() => {
    setSelectedDateKey(null);
  }, []);

  return (
    <div>
      {/* Week navigation */}
      <div className="card mb-4 lg-mobile:mb-6 p-3 xs:p-4 lg-mobile:p-5">
        {/* Week header */}
        <div className="flex items-center justify-between mb-3 lg-mobile:mb-4">
          <button
            type="button"
            onClick={goToPrevWeek}
            className="flex items-center justify-center w-9 h-9 lg-mobile:w-11 lg-mobile:h-11 rounded-xl text-slate-500 hover:bg-slate-100 hover:text-slate-700 transition-colors"
            aria-label="Предыдущая неделя"
          >
            <IconChevronLeft className="w-5 h-5 lg-mobile:w-6 lg-mobile:h-6" />
          </button>

          <div className="text-center">
            <button
              type="button"
              onClick={isWeekSelected ? goToToday : selectWholeWeek}
              className={`px-3 py-1.5 rounded-lg text-sm xs:text-base lg-mobile:text-lg font-medium transition-all ${
                isWeekSelected
                  ? "bg-[var(--color-accent)] text-white shadow-md"
                  : "text-slate-800 hover:bg-slate-100 active:bg-slate-200"
              }`}
            >
              {weekLabel}
            </button>
            {!isCurrentWeek && !isWeekSelected && (
              <button
                type="button"
                onClick={goToToday}
                className="flex items-center justify-center gap-1 mx-auto mt-0.5 text-xs lg-mobile:text-sm text-[var(--color-accent)] hover:underline"
                aria-label="Перейти к сегодня"
              >
                <IconChevronDown className="w-3.5 h-3.5 lg-mobile:w-4 lg-mobile:h-4" />
                Сегодня
              </button>
            )}
          </div>

          <button
            type="button"
            onClick={goToNextWeek}
            className="flex items-center justify-center w-9 h-9 lg-mobile:w-11 lg-mobile:h-11 rounded-xl text-slate-500 hover:bg-slate-100 hover:text-slate-700 transition-colors"
            aria-label="Следующая неделя"
          >
            <IconChevronRight className="w-5 h-5 lg-mobile:w-6 lg-mobile:h-6" />
          </button>
        </div>

        {/* Week days */}
        <WeekDaySelector
          weekDayDateKeys={weekDayDateKeys}
          selectedDateKey={selectedDateKey}
          onSelectDateKey={setSelectedDateKey}
          eventsByDay={eventsByDay}
          todayDateKey={todayDateKey}
        />
      </div>

      {/* Day agenda */}
      {isLoading ? (
        <div className="flex flex-col items-center justify-center min-h-[200px] py-12">
          <div className="w-6 h-6 border-2 border-slate-200 border-t-slate-500 rounded-full animate-spin mb-3" />
          <p className="text-xs text-slate-500">Загрузка...</p>
        </div>
      ) : error ? (
        <div className="card p-6 text-center">
          <p className="text-slate-600 text-sm mb-4">{error}</p>
          <button onClick={refetchEvents} className="btn-primary">
            Повторить
          </button>
        </div>
      ) : (
        <DayAgenda
          selectedDateKey={selectedDateKey}
          events={selectedEvents}
          todayDateKey={todayDateKey}
          weekLabel={weekLabel}
        />
      )}
    </div>
  );
}
